/**
 * SilentGuestMatchesBanner — iter203
 *
 * Dismissible banner shown on GroupDetailPage when one or more
 * silent-added guests (iter202) have signed up on SplitEasy. Lists
 * the matches in a modal and lets admins bulk-convert or convert
 * individually. Closes itself when count drops to 0.
 *
 * UX: emerald gradient + soft pulse to draw the eye without being noisy.
 */
import { useState, useCallback } from 'react';
import { Sparkles, X, Loader2, UserCheck, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';

export default function SilentGuestMatchesBanner({ groupId, matches, dismissed, setDismissed, refresh }) {
  const [open, setOpen] = useState(false);
  const [convertingId, setConvertingId] = useState(null);
  const [bulkConverting, setBulkConverting] = useState(false);
  const count = matches.length;

  const convertOne = useCallback(async (m) => {
    setConvertingId(m.guest_id);
    try {
      const r = await api.post(`/groups/${groupId}/silent-guests/convert`, {
        session_id: m.session_id,
        guest_id: m.guest_id,
        user_id: m.matched_user.id,
      });
      toast.success(r.data?.message || `${m.matched_user.name} converted`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Conversion failed');
    } finally {
      setConvertingId(null);
    }
  }, [groupId, refresh]);

  const convertAll = useCallback(async () => {
    if (matches.length === 0) return;
    setBulkConverting(true);
    let success = 0, failed = 0;
    // Sequential to keep server-side audit trail clean.
    for (const m of matches) {
      try {
        await api.post(`/groups/${groupId}/silent-guests/convert`, {
          session_id: m.session_id,
          guest_id: m.guest_id,
          user_id: m.matched_user.id,
        });
        success += 1;
      } catch {
        failed += 1;
      }
    }
    if (failed === 0) toast.success(`Linked ${success} guest${success === 1 ? '' : 's'} to their account`);
    else toast.warning(`Linked ${success}, ${failed} failed`);
    await refresh();
    setBulkConverting(false);
    if (failed === 0) setOpen(false);
  }, [groupId, matches, refresh]);

  if (count === 0 || dismissed) return null;

  return (
    <>
      <div
        className="rounded-2xl border-2 border-emerald-500/40 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 p-3 flex items-center gap-3 shadow-sm animate-in fade-in slide-in-from-top-2 duration-500"
        data-testid="silent-guest-matches-banner"
      >
        <div className="relative">
          <span className="absolute inset-0 rounded-full bg-emerald-500/30 animate-ping" />
          <span className="relative w-9 h-9 rounded-full bg-emerald-500/25 text-emerald-700 dark:text-emerald-300 inline-flex items-center justify-center">
            <Sparkles className="w-4 h-4" />
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex-1 text-left min-w-0"
          data-testid="silent-guest-matches-open"
        >
          <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200 truncate">
            {count} silent guest{count === 1 ? '' : 's'} just signed up
          </p>
          <p className="text-xs text-emerald-700/80 dark:text-emerald-300/80">
            Tap to link their visits to their account →
          </p>
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="w-7 h-7 rounded-full inline-flex items-center justify-center text-emerald-700/60 hover:bg-emerald-500/15 hover:text-emerald-800 transition-colors"
          title="Dismiss until next refresh"
          data-testid="silent-guest-matches-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <Dialog open={open} onOpenChange={(o) => !bulkConverting && setOpen(o)}>
        <DialogContent className="max-w-md" data-testid="silent-guest-matches-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-600" />
              Link guest visits to accounts
            </DialogTitle>
            <DialogDescription>
              These guests were added with a phone that has now signed up on SplitEasy.
              Linking puts their past guest fees in their own ledger — they stay guests, not group members.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[55vh] overflow-y-auto space-y-2 py-1" data-testid="silent-guest-matches-list">
            {matches.map((m) => (
              <div
                key={m.guest_id}
                className="rounded-xl border-2 border-border bg-card/40 p-3 flex items-center gap-3"
                data-testid={`silent-guest-row-${m.guest_id}`}
              >
                <span className="w-10 h-10 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 inline-flex items-center justify-center text-sm font-bold flex-shrink-0">
                  {(m.matched_user.name || '?').slice(0, 1).toUpperCase()}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{m.matched_user.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {m.matched_user.phone} · was added as <span className="font-medium">{m.guest_name}</span>
                  </p>
                  <p className="text-xs text-muted-foreground/80 truncate">
                    {m.session_title} · {m.session_date}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => convertOne(m)}
                  disabled={convertingId === m.guest_id || bulkConverting}
                  className="gap-1 flex-shrink-0"
                  data-testid={`silent-guest-convert-${m.guest_id}`}
                >
                  {convertingId === m.guest_id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <>
                      <UserCheck className="w-3.5 h-3.5" />
                      Link
                    </>
                  )}
                </Button>
              </div>
            ))}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={bulkConverting} data-testid="silent-guest-matches-close">
              Close
            </Button>
            {matches.length > 1 && (
              <Button
                onClick={convertAll}
                disabled={bulkConverting}
                className="gap-1.5"
                data-testid="silent-guest-convert-all"
              >
                {bulkConverting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ChevronRight className="w-3.5 h-3.5" />}
                {bulkConverting ? 'Converting…' : `Link all ${matches.length}`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
